
import { Device } from "@/api/dtos/Device.schema";
import { Component, Prop, Vue, ModelSync } from "vue-property-decorator";
import EditButton from "@/components/buttons/EditButton.vue";
import { getCurrentUtilization } from "@/api/stations";
import { CurrentDeviceStatusDto } from "@/api/dtos/CurrentDeviceStatusDto";
import { UsersModule } from "@/store/modules/UsersModule";
import { User } from "@/api/dtos/User.schema";

@Component({
  components: { EditButton },
})
export default class DeviceChooser extends Vue {
  @ModelSync("value", "input", { default: 1 }) deviceNumber!: number;

  @Prop() devices!: Device[];
  @Prop() stationId!: string;

  @Prop({ default: false })
  creationMode!: boolean;

  get localUser(): User | null {
    return UsersModule.localUser;
  }

  get isAdmin(): boolean {
    return this.localUser?.isAdmin || false;
  }

  private utilizationByDevice: Record<
    number,
    CurrentDeviceStatusDto | undefined
  > = {};
  private loadingUtilization = false;

  async mounted(): Promise<void> {
    await this.loadUtilization();
  }

  // must be public to call from template
  async loadUtilization(): Promise<void> {
    this.loadingUtilization = true;
    try {
      const list = await getCurrentUtilization(this.stationId);
      this.utilizationByDevice = list.reduce((acc, cur) => {
        acc[cur.deviceNumber] = cur;
        return acc;
      }, {} as Record<number, CurrentDeviceStatusDto>);
    } catch (e) {
      this.utilizationByDevice = {};
    } finally {
      this.loadingUtilization = false;
    }
  }

  get sortedDevices(): { [deviceNumber: number]: Device } {
    return this.devices.reduce<{ [deviceNumber: number]: Device }>(
      (accu, device) => {
        accu[device.deviceNumber] = device;
        return accu;
      },
      {}
    );
  }

  handleDeviceClick(deviceNumber: number): void {
    const deviceExists = !!this.sortedDevices[deviceNumber];

    // Only admins can click empty tiles
    if (!deviceExists && !this.isAdmin) return;

    this.deviceNumber = deviceNumber;
  }

  private getStatus(deviceNumber: number): {
    exists: boolean;
    occupied: boolean;
    isOpen: boolean;
    hasAlarm: boolean;
  } {
    const exists = !!this.sortedDevices[deviceNumber];
    const u = this.utilizationByDevice[deviceNumber];

    if (this.creationMode || !u) {
      return { exists, occupied: false, isOpen: false, hasAlarm: false };
    }

    const occupied = !!u.occupied;
    const isOpen = u.doorState !== "UNKNOWN" && u.doorState === "OPEN";
    const hasAlarm = u.alarmState !== "UNKNOWN" && u.alarmState === "ALARM";

    return { exists, occupied, isOpen, hasAlarm };
  }

  getDeviceHeaderColor(deviceNumber: number): "bpbDanger" | "bpbWarn" | "none" {
    if (this.creationMode) return "none";
    const { hasAlarm, isOpen } = this.getStatus(deviceNumber);
    if (hasAlarm) return "bpbDanger";
    if (isOpen) return "bpbWarn";
    return "none";
  }

  getDeviceTileColor(deviceNumber: number): string {
    const { exists, occupied } = this.getStatus(deviceNumber);
    const isDark = this.$vuetify.theme.dark;

    if (!exists) {
      return `${
        isDark
          ? this.$vuetify.theme.themes.dark.bpbLightGrey
          : this.$vuetify.theme.themes.light.bpbLightGrey
      }`;
    }

    if (occupied && !this.creationMode) {
      return `${
        isDark
          ? this.$vuetify.theme.themes.dark.bpbPrimaryLight
          : this.$vuetify.theme.themes.light.bpbPrimaryLight
      }`;
    }

    return isDark ? "#2c2c2c" : "white";
  }

  getDeviceOutline(deviceNumber: number): string {
    const isDark = this.$vuetify.theme.dark;
    if (deviceNumber === this.deviceNumber) {
      return `4px solid ${isDark ? "white" : "black"}`;
    }
    if (!this.sortedDevices[deviceNumber]) {
      return `2px solid ${
        isDark
          ? this.$vuetify.theme.themes.dark.bpbLightGrey
          : this.$vuetify.theme.themes.light.bpbLightGrey
      }`;
    }
    return `2px solid ${
      isDark
        ? this.$vuetify.theme.themes.dark.bpbPrimaryLight
        : this.$vuetify.theme.themes.light.bpbPrimaryLight
    }`;
  }

  getTextColor(deviceNumber: number): string {
    const { exists, occupied } = this.getStatus(deviceNumber);
    const isDark = this.$vuetify.theme.dark;

    if (!exists) {
      return `${
        isDark
          ? this.$vuetify.theme.themes.dark.bpbDarkGrey
          : this.$vuetify.theme.themes.light.bpbDarkGrey
      }`;
    }
    if (occupied && !this.creationMode) return "white";
    return isDark ? "white" : "black";
  }

  getHardwareUrl(deviceNumber: number): string {  
    const device = this.devices.find(d => d.deviceNumber === deviceNumber);
    if (!device) return "";

    const wts = device.hardwareSettings?.wts04Out?.wts04?.replace("WTS", "");
    const port = device.hardwareSettings?.wts04Out?.wts04OutPort?.replace("OUT", "");
    if (!wts || !port) return "";

    return `${parseInt(wts)}/${parseInt(port)}`; // remove leading 0s
  }

  updateSelectedDevice(deviceNumber: number): void {
    this.deviceNumber = deviceNumber;
  }
}
